/**
 * AGENT TOUT TERRAIN + TERRAIN IA — Dashboard Enterprise V5
 * ────────────────────────────────────────────────────────────
 * Design premium orienté décideurs (CEO, Directeur Commercial, Directeur Achats)
 * KPI SaaS : abonnements, revenus récurrents, agents IA actifs, intelligence marché
 * Branché Firebase en temps réel + Worker API
 */

import { useState, useEffect, useCallback } from "react";
import { initializeApp } from "firebase/app";
import { getFirestore, collection, onSnapshot } from "firebase/firestore";

// ─── CONFIG ──────────────────────────────────────────────────────────────────
const firebaseConfig = {
  apiKey: "AIzaSyAq-TdRbKhWss3fqO1rj7nOrVP60ivKIE8",
  authDomain: "agent-tout-terrain.firebaseapp.com",
  projectId: "agent-tout-terrain",
  storageBucket: "agent-tout-terrain.firebasestorage.app",
  messagingSenderId: "143560362456",
  appId: "1:143560362456:web:4503381dc96da746460aad",
};
const firebaseApp = initializeApp(firebaseConfig);
const db = getFirestore(firebaseApp);
const WORKER = "https://agent-tout-terrain.soihibaliousseni.workers.dev";

// ─── PALETTE ENTERPRISE ───────────────────────────────────────────────────────
const C = {
  bg:       "#060810",
  surface:  "#090b18",
  panel:    "#0d1020",
  card:     "#111428",
  border:   "#1a1f38",
  borderHi: "#252c50",
  // Accents
  teal:   "#00e5c0", tealD: "#00e5c018",
  gold:   "#f0c040", goldD: "#f0c04015",
  purple: "#8b5cf6", purpleD: "#8b5cf615",
  blue:   "#38bdf8", blueD:  "#38bdf815",
  green:  "#22c55e", greenD: "#22c55e15",
  red:    "#ef4444", redD:   "#ef444415",
  amber:  "#f59e0b", amberD: "#f59e0b15",
  // Texte
  txt:    "#e2e8f0",
  mid:    "#64748b",
  dim:    "#1e2440",
};

// ─── UTILITAIRES ──────────────────────────────────────────────────────────────
const money = n => (n || 0).toLocaleString("fr-FR") + " €";
const pct   = (a, b) => b ? Math.round((a / b) * 100) : 0;

// ─── COMPOSANTS DE BASE ───────────────────────────────────────────────────────

function Badge({ children, color = C.teal }) {
  return (
    <span style={{
      display: "inline-flex", alignItems: "center", gap: 4,
      padding: "3px 10px", borderRadius: 20,
      background: color + "20", border: `1px solid ${color}40`,
      color, fontSize: 11, fontWeight: 700, letterSpacing: ".5px",
      textTransform: "uppercase",
    }}>{children}</span>
  );
}

function KpiCard({ icon, label, value, sub, color = C.teal, trend }) {
  return (
    <div style={{
      background: C.card, border: `1px solid ${C.border}`,
      borderRadius: 14, padding: "16px 20px",
      display: "flex", flexDirection: "column", gap: 6,
      position: "relative", overflow: "hidden",
    }}>
      <div style={{
        position: "absolute", top: 0, left: 0, width: 3,
        height: "100%", background: color, borderRadius: "14px 0 0 14px",
      }} />
      <div style={{ fontSize: 11, color: C.mid, fontWeight: 600, textTransform: "uppercase", letterSpacing: ".5px" }}>
        {icon} {label}
      </div>
      <div style={{ fontSize: 26, fontWeight: 800, color, fontVariantNumeric: "tabular-nums" }}>
        {value}
      </div>
      {sub && <div style={{ fontSize: 11, color: C.mid }}>{sub}</div>}
      {trend != null && (
        <div style={{ fontSize: 11, color: trend >= 0 ? C.green : C.red }}>
          {trend >= 0 ? "↑" : "↓"} {Math.abs(trend)}% vs semaine dernière
        </div>
      )}
    </div>
  );
}

function SectionTitle({ children, action }) {
  return (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 14 }}>
      <div style={{ fontSize: 13, fontWeight: 700, color: C.txt, textTransform: "uppercase", letterSpacing: ".8px" }}>
        {children}
      </div>
      {action}
    </div>
  );
}

function Btn({ children, onClick, color = C.teal, size = "md" }) {
  const p = size === "sm" ? "6px 14px" : "10px 20px";
  const fs = size === "sm" ? 12 : 13;
  return (
    <button onClick={onClick} style={{
      padding: p, borderRadius: 9, border: "none", cursor: "pointer",
      background: color + "20", color, fontSize: fs, fontWeight: 700,
      border: `1px solid ${color}40`,
      transition: "all .15s",
    }}
    onMouseEnter={e => { e.target.style.background = color + "35"; }}
    onMouseLeave={e => { e.target.style.background = color + "20"; }}
    >{children}</button>
  );
}

function AgentCard({ icon, name, status, action, color = C.teal }) {
  const isActive = status === "actif";
  return (
    <div style={{
      background: C.card, border: `1px solid ${isActive ? color + "40" : C.border}`,
      borderRadius: 12, padding: "12px 16px",
      display: "flex", alignItems: "center", justifyContent: "space-between",
    }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
        <div style={{
          width: 36, height: 36, borderRadius: 10,
          background: color + "20", display: "flex", alignItems: "center",
          justifyContent: "center", fontSize: 18,
        }}>{icon}</div>
        <div>
          <div style={{ fontSize: 13, fontWeight: 700, color: C.txt }}>{name}</div>
          <div style={{ fontSize: 11, color: isActive ? color : C.mid }}>
            {isActive ? "● En ligne" : "○ En veille"}
          </div>
        </div>
      </div>
      {action && <Btn onClick={action} size="sm" color={color}>Lancer</Btn>}
    </div>
  );
}


const PRODUCT_LABELS = {
  agenttoutterrain: "Agent Tout Terrain",
  terrainia:        "Terrain IA",
  siteweb:          "Site web",
  appdev:           "App mobile",
  automatisation:   "Automatisation",
  integratioia:     "Chatbot IA",
};
const productLabel = p => PRODUCT_LABELS[p] || "Agent Tout Terrain";

function ProspectRow({ opp, onStatusChange }) {
  const [expanded, setExpanded] = useState(false);
  const score = opp.score || 60;
  const scoreColor = score >= 75 ? C.green : score >= 55 ? C.amber : C.red;
  const scoreBadge = score >= 75 ? "🔥" : score >= 55 ? "🟢" : score >= 35 ? "🟡" : "⚫";
  const prob = opp.probabiliteAchat || opp.conversion || 50;

  const STATUTS = ["Nouveau", "Qualifié", "RDV", "Devis", "Signature", "Gagné", "Perdu"];
  const statusColors = { Nouveau: C.mid, Qualifié: C.blue, Négociation: C.amber, RDV: C.purple, Devis: C.gold, Signature: C.teal, Gagné: C.green, Perdu: C.red };

  // Badge "Nouveau" — moins de 24h depuis la détection
  const isNew = opp.detectedAt && (Date.now() - new Date(opp.detectedAt).getTime()) < 24 * 60 * 60 * 1000;

  const changeStatus = async (statut) => {
    try {
      await fetch(`${WORKER}/opportunity/${opp.id}/statut`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ statut }),
      });
      if (onStatusChange) onStatusChange(opp.id, statut);
    } catch {}
  };

  return (
    <div style={{ borderBottom: `1px solid ${C.border}`, borderLeft: opp.status && opp.status !== "Nouveau" ? `3px solid ${statusColors[opp.status] || C.mid}` : "3px solid transparent" }}>
      <div onClick={() => setExpanded(!expanded)} style={{
        display: "grid", gridTemplateColumns: "2fr 1fr 1fr 1fr 100px",
        alignItems: "center", gap: 12, padding: "10px 16px", cursor: "pointer",
      }}>
        <div>
          <div style={{ fontSize: 13, fontWeight: 600, color: C.txt, display: "flex", alignItems: "center", gap: 6 }}>
            {opp.company}
            {isNew && (
              <span style={{ fontSize: 9, fontWeight: 800, color: C.green, background: C.green + "20", padding: "1px 6px", borderRadius: 20, border: `1px solid ${C.green}40` }}>🆕 NOUVEAU</span>
            )}
          </div>
          <div style={{ fontSize: 11, color: C.mid }}>{opp.region} · {opp.sector}
            {opp.status && opp.status !== "Nouveau" && (
              <span style={{ marginLeft: 6, color: statusColors[opp.status] || C.mid, fontWeight: 700 }}>· {opp.status}</span>
            )}
          </div>
        </div>
        <div style={{ fontSize: 12, color: C.mid }}>{productLabel(opp.bestProduct)}</div>
        <div>
          <span style={{
            display: "inline-block", padding: "2px 8px", borderRadius: 20,
            background: scoreColor + "20", color: scoreColor,
            fontSize: 12, fontWeight: 700,
          }}>{scoreBadge} {score}/100</span>
        </div>
        <div>
          <div style={{ fontSize: 12, color: C.txt, marginBottom: 3 }}>{prob}%</div>
          <div style={{ height: 3, borderRadius: 2, background: C.border }}>
            <div style={{ width: `${prob}%`, height: 3, borderRadius: 2, background: C.teal }} />
          </div>
        </div>
        <div style={{ fontSize: 13, fontWeight: 700, color: C.gold, textAlign: "right" }}>
          {money(opp.budget || 1000)}
        </div>
      </div>

      {expanded && (
        <div style={{ padding: "0 16px 14px 16px", background: C.card }}>
          {/* Détails score IA */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 10, fontSize: 12 }}>
            <div><span style={{ color: C.mid }}>Décideur probable :</span> <b>{opp.decideurProbable || "Gérant/Dirigeant"}</b></div>
            <div><span style={{ color: C.mid }}>Maturité digitale :</span> <b>{opp.maturiteDigitale || "moyenne"}</b></div>
            <div style={{ gridColumn: "1 / -1" }}><span style={{ color: C.mid }}>Pourquoi maintenant :</span> {opp.pourquoiMaintenant || opp.strategy || "—"}</div>
            <div style={{ gridColumn: "1 / -1" }}><span style={{ color: C.mid }}>Besoin probable :</span> {opp.besoinProbable || "—"}</div>
            {opp.objectionPrincipale && (
              <div style={{ gridColumn: "1 / -1", background: C.panel, borderRadius: 8, padding: 8 }}>
                <span style={{ color: C.red }}>Objection probable :</span> {opp.objectionPrincipale}<br/>
                <span style={{ color: C.green }}>Réponse :</span> {opp.reponseObjection}
              </div>
            )}
          </div>
          {/* Boutons statut CRM */}
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
            {STATUTS.map(s => (
              <button key={s} onClick={(e) => { e.stopPropagation(); changeStatus(s); }} style={{
                padding: "4px 10px", borderRadius: 20, fontSize: 11, fontWeight: 700, cursor: "pointer",
                background: opp.status === s ? (statusColors[s]||C.mid) : "transparent",
                color: opp.status === s ? "#fff" : (statusColors[s]||C.mid),
                border: `1px solid ${statusColors[s]||C.mid}60`,
              }}>{s}</button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function PipelineCol({ title, count, value, color }) {
  return (
    <div style={{
      background: C.card, border: `1px solid ${color}30`,
      borderRadius: 12, padding: "14px 16px", textAlign: "center",
    }}>
      <div style={{ fontSize: 11, color, fontWeight: 700, textTransform: "uppercase", letterSpacing: ".5px", marginBottom: 6 }}>
        {title}
      </div>
      <div style={{ fontSize: 28, fontWeight: 800, color, marginBottom: 4 }}>{count}</div>
      {value > 0 && <div style={{ fontSize: 11, color: C.mid }}>{money(value)}</div>}
    </div>
  );
}

// ─── TABS ─────────────────────────────────────────────────────────────────────
const TABS = [
  { id: "dashboard", label: "Vue d'ensemble", icon: "⚡" },
  { id: "agents",    label: "Agents IA",       icon: "🤖" },
  { id: "whatsapp",  label: "WhatsApp",         icon: "💬" },
  { id: "prospects", label: "Prospects",        icon: "🎯" },
  { id: "marche",    label: "Intelligence Marché", icon: "🌍" },
  { id: "pipeline",  label: "Pipeline",         icon: "📊" },
];

// ─── APP PRINCIPALE ───────────────────────────────────────────────────────────
export default function DashboardEnterprise() {
  const [tab, setTab]         = useState("dashboard");
  const [opps, setOpps]       = useState([]);
  const [waList, setWaList]   = useState([]);
  const [waLoading, setWaLoading] = useState(false);
  const [selected, setSelected] = useState(new Set());

  // Liens Stripe
  const STRIPE = {
    starter:    "https://buy.stripe.com/bJefZh30l2cO2WifQxbwk06",
    pro:        "https://buy.stripe.com/3cIaEX30l5p00OaeMtbwk07",
    enterprise: "https://buy.stripe.com/9B64gz1Wh8Bc9kG0VDbwk08",
  };
  const [status, setStatus]   = useState(null);
  const [insights, setInsights] = useState(null);
  const [scanLog, setScanLog] = useState([]);
  const [loading, setLoading] = useState({});
  const [marcheMode, setMarcheMode] = useState("analyse");
  const [marcheInput, setMarcheInput] = useState({ sujet: "", origine: "La Réunion", pays: "Madagascar", region: "" });
  const [marcheResult, setMarcheResult] = useState(null);
  const [marcheLoading, setMarcheLoading] = useState(false);
  const [now, setNow] = useState(new Date());
  const [online, setOnline] = useState(navigator.onLine);
  useEffect(() => {
    const on = () => setOnline(true);
    const off = () => setOnline(false);
    window.addEventListener('online', on);
    window.addEventListener('offline', off);
    return () => { window.removeEventListener('online', on); window.removeEventListener('offline', off); };
  }, []);

  // Horloge
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(t);
  }, []);

  // Firebase realtime
  useEffect(() => {
    const q = collection(db, "opportunities");
    return onSnapshot(q, snap => {
      setOpps(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    });
  }, []);

  // Status Worker
  useEffect(() => {
    fetch(`${WORKER}/status`)
      .then(r => r.json()).then(setStatus).catch(() => {});
    fetch(`${WORKER}/insights`)
      .then(r => r.json()).then(setInsights).catch(() => {});
  }, []);

  // Charger la liste WhatsApp
  const loadWhatsApp = useCallback(async () => {
    setWaLoading(true);
    try {
      // 1. Reset les anciens messages (pour forcer Claude à régénérer)
      await fetch(`${WORKER}/reset-whatsapp`);
      // 2. Préparer les nouveaux avec Claude
      await fetch(`${WORKER}/whatsapp`);
      await new Promise(r => setTimeout(r, 5000));
      // 3. Charger la liste complète
      const r = await fetch(`${WORKER}/whatsapp-list`);
      const d = await r.json();
      setWaList(d.prospects || []);
      setSelected(new Set());
    } catch {}
    setWaLoading(false);
  }, []);

  // Charger WhatsApp quand on arrive sur l'onglet
  useEffect(() => {
    if (tab === "whatsapp" && waList.length === 0) loadWhatsApp();
  }, [tab]);
  const total     = opps.length;
  const nouveau   = opps.filter(o => o.status === "Nouveau").length;
  const qualifie  = opps.filter(o => o.status === "Qualifié").length;
  const propose   = opps.filter(o => o.status === "Proposé").length;
  const nego      = opps.filter(o => o.status === "Négociation").length;
  const gagne     = opps.filter(o => o.status === "Gagné").length;
  const perdu     = opps.filter(o => o.status === "Perdu").length;
  const pipeline  = opps.filter(o => !["Perdu","Gagné"].includes(o.status)).reduce((s,o)=>s+(o.budget||0),0);
  const ca        = opps.filter(o => o.status === "Gagné").reduce((s,o)=>s+(o.budget||0),0);
  const withEmail = opps.filter(o => o.contactEmail).length;
  const sent      = opps.filter(o => o.firstEmailSentAt).length;
  const replied   = opps.filter(o => o.hasReplied).length;
  const top5      = [...opps].filter(o=>!["Perdu","Gagné"].includes(o.status)).sort((a,b)=>(b.score||0)-(a.score||0)).slice(0,5);
  const byProduct = opps.reduce((acc,o)=>{const p=o.bestProduct||"inconnu";acc[p]=(acc[p]||0)+1;return acc;},{});
  const att       = byProduct["agenttoutterrain"]||0;
  const terrainia = byProduct["terrainia"]||0;

  // Actions rapides
  const action = useCallback(async (route, label) => {
    setLoading(l => ({ ...l, [route]: true }));
    setScanLog(p => [...p, { text: `→ ${label}...`, ts: new Date() }]);
    try {
      const r = await fetch(`${WORKER}/${route}`);
      const d = await r.json();
      setScanLog(p => [...p, { text: `✅ ${label} : ${d.message || "OK"}`, ts: new Date() }]);
    } catch {
      setScanLog(p => [...p, { text: `❌ ${label} : erreur`, ts: new Date() }]);
    }
    setLoading(l => ({ ...l, [route]: false }));
  }, []);

  // Module Marché
  const lancerMarche = async () => {
    setMarcheLoading(true);
    setMarcheResult(null);
    const p = marcheInput;
    let url = `${WORKER}/enterprise/${marcheMode}?sujet=${encodeURIComponent(p.sujet)}`;
    if (marcheMode === "ou-vendre")   url += `&origine=${encodeURIComponent(p.origine)}`;
    if (marcheMode === "comparateur") url += `&pays=${encodeURIComponent(p.pays)}`;
    if (marcheMode === "veille")      url += `&region=${encodeURIComponent(p.region)}`;
    if (marcheMode === "fournisseur") url += `&pays=${encodeURIComponent(p.pays)}`;
    try {
      const r = await fetch(url);
      setMarcheResult(await r.json());
    } catch (e) {
      setMarcheResult({ error: e.message });
    }
    setMarcheLoading(false);
  };

  // ─── RENDER ───────────────────────────────────────────────────────────────
  return (
    <div style={{ background: C.bg, minHeight: "100vh", color: C.txt, fontFamily: "'Inter','Segoe UI',system-ui,sans-serif" }}>

      {/* TOP BAR */}
      <div style={{
        background: C.surface, borderBottom: `1px solid ${C.border}`,
        padding: "0 24px", display: "flex", alignItems: "center",
        justifyContent: "space-between", height: 54, position: "sticky", top: 0, zIndex: 50,
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <div style={{ width: 28, height: 28, borderRadius: 8, background: `linear-gradient(135deg,${C.teal},${C.purple})`, display:"flex",alignItems:"center",justifyContent:"center",fontSize:14 }}>🤖</div>
            <div>
              <span style={{ fontWeight: 800, fontSize: 14, color: C.txt }}>Agent Tout Terrain</span>
              <span style={{ fontSize: 11, color: C.mid, marginLeft: 8 }}>+ Terrain IA · Enterprise</span>
            </div>
          </div>
          {online ? <Badge color={C.green}>● EN LIGNE</Badge> : <Badge color={C.amber}>⚠️ HORS LIGNE — sync en attente</Badge>}
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 20 }}>
          <div style={{ fontSize: 12, color: C.mid }}>
            {now.toLocaleTimeString("fr-FR")} · {now.toLocaleDateString("fr-FR")}
          </div>
          <Btn onClick={() => action("scan", "Scan")} size="sm" color={C.teal}>⚡ Scanner</Btn>
          <Btn onClick={() => action("pipeline", "Pipeline")} size="sm" color={C.purple}>🔄 Cycle complet</Btn>
        </div>
      </div>

      {/* TABS */}
      <div style={{ background: C.surface, borderBottom: `1px solid ${C.border}`, padding: "0 24px", display: "flex", gap: 4 }}>
        {TABS.map(t => (
          <button key={t.id} onClick={() => setTab(t.id)} style={{
            padding: "12px 16px", border: "none", cursor: "pointer", background: "transparent",
            color: tab === t.id ? C.teal : C.mid,
            borderBottom: tab === t.id ? `2px solid ${C.teal}` : "2px solid transparent",
            fontWeight: tab === t.id ? 700 : 500, fontSize: 13,
            transition: "all .15s",
          }}>{t.icon} {t.label}</button>
        ))}
      </div>

      <div style={{ padding: "24px", maxWidth: 1400, margin: "0 auto" }}>

        {/* ═══ WHATSAPP ═══ */}
        {tab === "whatsapp" && (
          <div>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 20 }}>
              <div>
                <div style={{ fontSize: 22, fontWeight: 800, marginBottom: 4 }}>💬 Contacts WhatsApp</div>
                <div style={{ fontSize: 13, color: C.mid }}>Messages rédigés par Claude · Classés par produit · Coche et envoie</div>
              </div>
              <Btn onClick={loadWhatsApp} color={C.green}>
                {waLoading ? "⏳ Préparation..." : "🔄 Actualiser"}
              </Btn>
            </div>

            {/* Stats */}
            <div style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 12, marginBottom: 20 }}>
              <KpiCard icon="📱" label="Total contacts" value={waList.length} color={C.green} />
              <KpiCard icon="🏭" label="Terrain IA" value={waList.filter(w => ["terrainia"].includes(w.product) || ["usine_agro","industrie","btp","logistique_ind","laboratoire","agence_interim","usine_cosmetique","usine_textile"].includes(w.sector)).length} color={C.purple} />
              <KpiCard icon="🤖" label="Agent Tout Terrain" value={waList.filter(w => ["agenttoutterrain","att_starter","att_pro","att_enterprise"].includes(w.product) || ["agence_immo","import_export","grossiste","transporteur","agro_negoce","consulting"].includes(w.sector)).length} color={C.teal} />
              <KpiCard icon="🌐" label="Site web / Autres" value={waList.filter(w => ["siteweb","integratioia","automatisation"].includes(w.product)).length} color={C.gold} />
            </div>

            {waLoading && (
              <div style={{ background: C.panel, border: `1px solid ${C.amber}40`, borderRadius: 14, padding: 24, textAlign: "center", color: C.amber }}>
                ⏳ Claude prépare les messages... (30 secondes)
              </div>
            )}

            {!waLoading && waList.length === 0 && (
              <div style={{ background: C.panel, border: `1px solid ${C.border}`, borderRadius: 14, padding: 32, textAlign: "center" }}>
                <div style={{ fontSize: 32, marginBottom: 12 }}>📱</div>
                <div style={{ color: C.mid, fontSize: 14, marginBottom: 16 }}>Aucun contact prêt · Clique sur Actualiser</div>
                <Btn onClick={loadWhatsApp} color={C.green}>Préparer les messages</Btn>
              </div>
            )}

            {waList.length > 0 && (() => {
              // Grouper par produit
              const groups = {
                terrainia: { label: "🏭 Terrain IA", color: C.purple, items: [] },
                att: { label: "🤖 Agent Tout Terrain", color: C.teal, items: [] },
                integratioia: { label: "🤖 Chatbot IA", color: C.blue, items: [] },
                siteweb: { label: "🌐 Site web", color: C.gold, items: [] },
                autres: { label: "⚙️ Automatisation", color: C.mid, items: [] },
              };
              const industrialSectors = ["usine_agro","industrie","btp","logistique_ind","laboratoire","agence_interim","usine_cosmetique","usine_textile"];
              const attSectors = ["agence_immo","import_export","grossiste","transporteur","agro_negoce","consulting","tech_pme","ecommerce","agence_marketing"];
              const sitewebSectors = ["restaurant","hotel","garage","salon_coiffure","boulangerie","epicerie","magasin_mode","magasin_bio"];
              const chatbotSectors = ["cabinet_medical","pharmacie","dentiste","veterinaire"];
              const autoSectors = ["comptable","notaire","rh"];
              waList.forEach(w => {
                const p = w.product || "";
                const s = w.sector || "";
                if (p === "terrainia" || industrialSectors.includes(s)) groups.terrainia.items.push(w);
                else if (["agenttoutterrain","att_starter","att_pro","att_enterprise"].includes(p) || attSectors.includes(s)) groups.att.items.push(w);
                else if (p === "integratioia" || chatbotSectors.includes(s)) groups.integratioia.items.push(w);
                else if (p === "siteweb" || sitewebSectors.includes(s)) groups.siteweb.items.push(w);
                else groups.autres.items.push(w);
              });

              const toggleAll = (items, checked) => {
                setSelected(prev => {
                  const next = new Set(prev);
                  items.forEach(w => checked ? next.add(w.phone) : next.delete(w.phone));
                  return next;
                });
              };
              const toggle = phone => setSelected(prev => {
                const next = new Set(prev);
                next.has(phone) ? next.delete(phone) : next.add(phone);
                return next;
              });

              return (
                <div>
                  {/* Barre d'actions globales */}
                  <div style={{ background: C.panel, border: `1px solid ${C.border}`, borderRadius: 12, padding: "12px 16px", marginBottom: 16, display: "flex", alignItems: "center", gap: 12 }}>
                    <input type="checkbox"
                      onChange={e => toggleAll(waList, e.target.checked)}
                      style={{ width: 16, height: 16, cursor: "pointer" }} />
                    <span style={{ fontSize: 13, color: C.mid }}>Tout sélectionner</span>
                    <span style={{ fontSize: 13, color: C.teal, fontWeight: 700 }}>{selected.size} sélectionné(s)</span>
                    {selected.size > 0 && (
                      <div style={{ marginLeft: "auto", display: "flex", gap: 8 }}>
                        <span style={{ fontSize: 12, color: C.mid }}>
                          Ouvre les liens un par un →
                        </span>
                        {waList.filter(w => selected.has(w.phone)).map((w, i) => (
                          <a key={i} href={w.link} target="_blank" rel="noopener noreferrer"
                            style={{ padding: "6px 12px", background: "#25D366", color: "#fff", borderRadius: 8, fontSize: 12, fontWeight: 700, textDecoration: "none" }}>
                            📲 {w.company?.slice(0,15)}
                          </a>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Sections par produit */}
                  {Object.entries(groups).map(([key, group]) => {
                    if (group.items.length === 0) return null;
                    const allChecked = group.items.every(w => selected.has(w.phone));
                    return (
                      <div key={key} style={{ marginBottom: 20 }}>
                        {/* En-tête section */}
                        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10, padding: "8px 12px", background: group.color + "15", borderRadius: 10, border: `1px solid ${group.color}30` }}>
                          <input type="checkbox" checked={allChecked}
                            onChange={e => toggleAll(group.items, e.target.checked)}
                            style={{ width: 16, height: 16, cursor: "pointer" }} />
                          <span style={{ fontSize: 14, fontWeight: 700, color: group.color }}>{group.label}</span>
                          <span style={{ fontSize: 12, color: C.mid }}>· {group.items.length} contact{group.items.length > 1 ? "s" : ""}</span>
                        </div>

                        {/* Cartes contacts */}
                        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                          {group.items.map((w, i) => {
                            const isSelected = selected.has(w.phone);
                            return (
                              <div key={i} style={{
                                background: isSelected ? group.color + "10" : C.card,
                                border: `1px solid ${isSelected ? group.color + "50" : C.border}`,
                                borderRadius: 12, padding: "12px 16px",
                                display: "grid", gridTemplateColumns: "auto 1fr auto",
                                gap: 12, alignItems: "start",
                                transition: ".15s",
                              }}>
                                {/* Case à cocher */}
                                <input type="checkbox" checked={isSelected}
                                  onChange={() => toggle(w.phone)}
                                  style={{ width: 16, height: 16, cursor: "pointer", marginTop: 4 }} />

                                {/* Infos + message */}
                                <div>
                                  <div style={{ fontWeight: 700, fontSize: 13, color: C.txt, marginBottom: 3 }}>
                                    {w.company}
                                    {w.isRelance && <span style={{ marginLeft: 8, fontSize: 10, background: C.amber+"20", color: C.amber, padding: "2px 8px", borderRadius: 20, fontWeight: 700 }}>🔄 RELANCE J+3</span>}
                                  </div>
                                  <div style={{ fontSize: 11, color: C.mid, marginBottom: 8 }}>
                                    {w.region} · {w.phone} {w.lang && `· 🌍 ${w.lang}`}
                                  </div>
                                  <div style={{
                                    background: "#0a1a0a", border: `1px solid #1a3a1a`,
                                    borderRadius: 8, padding: "8px 12px",
                                    fontSize: 12, color: "#b8d4b8",
                                    whiteSpace: "pre-wrap", lineHeight: 1.5,
                                    maxHeight: 90, overflow: "auto",
                                  }}>
                                    {w.message || "Message en cours..."}
                                  </div>
                                  {/* Liens Stripe si produit ATT */}
                                  {(w.product === "agenttoutterrain" || w.product === "att_pro" || w.product === "att_enterprise") && (
                                    <div style={{ marginTop: 6, display: "flex", gap: 4, flexWrap: "wrap" }}>
                                      <a href={STRIPE.starter} target="_blank" rel="noopener noreferrer" style={{ fontSize: 10, color: C.mid, padding: "3px 7px", borderRadius: 6, border: `1px solid ${C.border}`, textDecoration: "none" }}>💳 Starter 299€</a>
                                      <a href={STRIPE.pro} target="_blank" rel="noopener noreferrer" style={{ fontSize: 10, color: C.teal, padding: "3px 7px", borderRadius: 6, border: `1px solid ${C.teal}40`, textDecoration: "none" }}>💳 Pro 699€</a>
                                      <a href={STRIPE.enterprise} target="_blank" rel="noopener noreferrer" style={{ fontSize: 10, color: C.gold, padding: "3px 7px", borderRadius: 6, border: `1px solid ${C.gold}40`, textDecoration: "none" }}>💳 Enterprise 1299€</a>
                                    </div>
                                  )}
                                </div>

                              {/* Bouton envoyer */}
                                <div style={{ display: "flex", flexDirection: "column", gap: 6, minWidth: 100 }}>
                                  <a href={w.link} target="_blank" rel="noopener noreferrer"
                                    onClick={() => {
                                      // Marquer comme envoyé → disparaît de la liste
                                      fetch(`${WORKER}/whatsapp-sent/${w.id}`, { method: "POST" })
                                        .then(() => {
                                          setWaList(prev => prev.filter(x => x.id !== w.id));
                                        });
                                    }}
                                    style={{
                                      display: "flex", flexDirection: "column", alignItems: "center", gap: 4,
                                      padding: "10px 14px", borderRadius: 10,
                                      background: w.isRelance ? C.amber : "#25D366",
                                      color: w.isRelance ? "#20293A" : "#fff",
                                      fontWeight: 800, fontSize: 13, textDecoration: "none",
                                      minWidth: 90, textAlign: "center",
                                    }}>
                                    {w.isRelance ? "🔄 Relancer" : "📲 Envoyer"}
                                  </a>
                                  <div style={{ fontSize: 9, color: C.mid, textAlign: "center" }}>
                                    {w.isRelance ? "Relance J+3" : "Disparaît après envoi"}
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })}
                </div>
              );
            })()}
          </div>
        )}

        {/* ═══ VUE D'ENSEMBLE ═══ */}
        {tab === "dashboard" && (
          <div>
            {/* KPI LIGNE 1 — Chiffre d'affaires */}
            <SectionTitle>💰 Revenus & pipeline</SectionTitle>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 12, marginBottom: 24 }}>
              <KpiCard icon="💰" label="Pipeline total"     value={money(pipeline)}  color={C.teal}   sub={`${total} prospects actifs`} />
              <KpiCard icon="🏆" label="CA Gagné"           value={money(ca)}        color={C.gold}   sub={`${gagne} contrats signés`} />
              <KpiCard icon="📈" label="En négociation"     value={money(nego * 1500)} color={C.purple} sub={`${nego} dossiers chauds`} />
              <KpiCard icon="🎯" label="Taux de conversion" value={`${pct(gagne, sent)}%`} color={C.green} sub={`${sent} contactés · ${replied} réponses`} />
            </div>

            {/* OFFRES STRIPE */}
            <SectionTitle>💳 Abonnements — Vendre maintenant</SectionTitle>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 12, marginBottom: 24 }}>
              {[
                { nom: "Starter", prix: "299€/mois", color: C.mid, url: STRIPE.starter, desc: "Prospection + emails + WhatsApp · 1 secteur" },
                { nom: "Pro", prix: "699€/mois", color: C.teal, url: STRIPE.pro, desc: "+ Intelligence Marché IA · 3 secteurs" },
                { nom: "Enterprise", prix: "1299€/mois", color: C.gold, url: STRIPE.enterprise, desc: "Tout inclus · 30 pays · 32 secteurs · Support prioritaire" },
              ].map(p => (
                <div key={p.nom} style={{ background: C.card, border: `1px solid ${p.color}40`, borderRadius: 14, padding: 16 }}>
                  <div style={{ fontSize: 12, fontWeight: 700, color: p.color, textTransform: "uppercase", letterSpacing: ".5px" }}>{p.nom}</div>
                  <div style={{ fontSize: 24, fontWeight: 800, color: p.color, margin: "4px 0" }}>{p.prix}</div>
                  <div style={{ fontSize: 11, color: C.mid, marginBottom: 12 }}>{p.desc}</div>
                  <a href={p.url} target="_blank" rel="noopener noreferrer" style={{
                    display: "block", textAlign: "center", padding: "8px",
                    borderRadius: 8, background: p.color + "20",
                    border: `1px solid ${p.color}40`, color: p.color,
                    fontWeight: 700, fontSize: 12, textDecoration: "none",
                  }}>💳 Lien paiement Stripe →</a>
                </div>
              ))}
            </div>

            {/* KPI LIGNE 2 — Produits */}
            <SectionTitle>📦 Ventes par produit</SectionTitle>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 12, marginBottom: 24 }}>
              <KpiCard icon="🤖" label="Agent Tout Terrain" value={att}       color={C.teal}   sub="Prospects ciblés · 249€+/mois" />
              <KpiCard icon="🏭" label="Terrain IA"         value={terrainia} color={C.purple} sub="Industrie · 299–1999€/mois" />
              <KpiCard icon="🌍" label="Marchés détectés"   value="22"        color={C.blue}   sub="Pays couverts · 3 scans/jour" />
            </div>

            {/* KPI LIGNE 3 — Agents + intelligence marché simulée */}
            <SectionTitle>🤖 Intelligence IA détectée</SectionTitle>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(5,1fr)", gap: 10, marginBottom: 24 }}>
              <div style={{ background: C.card, border: `1px solid ${C.green}30`, borderRadius: 12, padding: "14px", textAlign: "center" }}>
                <div style={{ fontSize: 22, marginBottom: 4 }}>💶</div>
                <div style={{ fontSize: 11, color: C.mid, marginBottom: 4, textTransform: "uppercase", fontWeight: 600 }}>Économies potentielles</div>
                <div style={{ fontSize: 20, fontWeight: 800, color: C.green }}>{money(pipeline * 0.12)}</div>
                <div style={{ fontSize: 10, color: C.mid, marginTop: 3 }}>sur vos achats (estimé)</div>
              </div>
              <div style={{ background: C.card, border: `1px solid ${C.teal}30`, borderRadius: 12, padding: "14px", textAlign: "center" }}>
                <div style={{ fontSize: 22, marginBottom: 4 }}>🏭</div>
                <div style={{ fontSize: 11, color: C.mid, marginBottom: 4, textTransform: "uppercase", fontWeight: 600 }}>Fournisseurs trouvés</div>
                <div style={{ fontSize: 20, fontWeight: 800, color: C.teal }}>18+</div>
                <div style={{ fontSize: 10, color: C.mid, marginTop: 3 }}>dernière recherche vanille MG</div>
              </div>
              <div style={{ background: C.card, border: `1px solid ${C.amber}30`, borderRadius: 12, padding: "14px", textAlign: "center" }}>
                <div style={{ fontSize: 22, marginBottom: 4 }}>⚠️</div>
                <div style={{ fontSize: 11, color: C.mid, marginBottom: 4, textTransform: "uppercase", fontWeight: 600 }}>Risques détectés</div>
                <div style={{ fontSize: 20, fontWeight: 800, color: C.amber }}>3</div>
                <div style={{ fontSize: 10, color: C.mid, marginTop: 3 }}>qualité + géopolitique</div>
              </div>
              <div style={{ background: C.card, border: `1px solid ${C.blue}30`, borderRadius: 12, padding: "14px", textAlign: "center" }}>
                <div style={{ fontSize: 22, marginBottom: 4 }}>🌐</div>
                <div style={{ fontSize: 11, color: C.mid, marginBottom: 4, textTransform: "uppercase", fontWeight: 600 }}>Marchés recommandés</div>
                <div style={{ fontSize: 20, fontWeight: 800, color: C.blue }}>5</div>
                <div style={{ fontSize: 10, color: C.mid, marginTop: 3 }}>module Où vendre ?</div>
              </div>
              <div style={{ background: C.card, border: `1px solid ${C.purple}30`, borderRadius: 12, padding: "14px", textAlign: "center" }}>
                <div style={{ fontSize: 22, marginBottom: 4 }}>🚚</div>
                <div style={{ fontSize: 11, color: C.mid, marginBottom: 4, textTransform: "uppercase", fontWeight: 600 }}>Réduction transport</div>
                <div style={{ fontSize: 20, fontWeight: 800, color: C.purple }}>12%</div>
                <div style={{ fontSize: 10, color: C.mid, marginTop: 3 }}>comparateur fournisseurs</div>
              </div>
            </div>

            {/* Pipeline + Top prospects */}
            <div style={{ display: "grid", gridTemplateColumns: "1fr 2fr", gap: 16 }}>
              {/* Pipeline */}
              <div style={{ background: C.panel, border: `1px solid ${C.border}`, borderRadius: 14, padding: 16 }}>
                <SectionTitle>📊 Pipeline live</SectionTitle>
                <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                  {[
                    { label: "Nouveau",       val: nouveau, color: C.blue },
                    { label: "Qualifié",      val: qualifie, color: C.teal },
                    { label: "Proposé",       val: propose, color: C.amber },
                    { label: "Négociation",   val: nego, color: C.purple },
                    { label: "Gagné",         val: gagne, color: C.green },
                    { label: "Perdu",         val: perdu, color: C.red },
                  ].map(({ label, val, color }) => (
                    <div key={label} style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <div style={{ width: 8, height: 8, borderRadius: "50%", background: color }} />
                        <span style={{ fontSize: 13, color: C.mid }}>{label}</span>
                      </div>
                      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                        <div style={{ width: 80, height: 4, borderRadius: 2, background: C.border }}>
                          <div style={{ width: `${pct(val, total) * 0.8}%`, minWidth: val > 0 ? 6 : 0, height: 4, borderRadius: 2, background: color }} />
                        </div>
                        <span style={{ fontSize: 13, fontWeight: 700, color, width: 24, textAlign: "right" }}>{val}</span>
                      </div>
                    </div>
                  ))}
                </div>
                <div style={{ marginTop: 14, paddingTop: 12, borderTop: `1px solid ${C.border}` }}>
                  <div style={{ fontSize: 11, color: C.mid, marginBottom: 6 }}>Logs Cloudflare</div>
                  <div style={{ maxHeight: 80, overflowY: "auto" }}>
                    {scanLog.slice(-5).reverse().map((l, i) => (
                      <div key={i} style={{ fontSize: 10, color: C.mid, padding: "1px 0" }}>
                        {l.ts.toLocaleTimeString("fr-FR")} {l.text}
                      </div>
                    ))}
                    {scanLog.length === 0 && <div style={{ fontSize: 10, color: C.dim }}>Aucune action récente</div>}
                  </div>
                </div>
              </div>

              {/* Top prospects */}
              <div style={{ background: C.panel, border: `1px solid ${C.border}`, borderRadius: 14, padding: 16 }}>
                <SectionTitle>🎯 Meilleures opportunités</SectionTitle>
                <div style={{ fontSize: 11, color: C.mid, marginBottom: 10, display: "grid", gridTemplateColumns: "2fr 1fr 1fr 1fr 100px" }}>
                  <span>Entreprise</span><span>Produit</span><span>Score IA</span><span>Prob.</span><span style={{textAlign:"right"}}>Valeur</span>
                </div>
                {top5.length > 0
                  ? top5.map(o => <ProspectRow key={o.id} opp={o} />)
                  : <div style={{ color: C.mid, fontSize: 13, padding: "20px 0", textAlign: "center" }}>
                      Lance un scan pour détecter des prospects
                    </div>
                }
              </div>
            </div>
          </div>
        )}

        {/* ═══ AGENTS IA ═══ */}
        {tab === "agents" && (
          <div>
            <SectionTitle>🤖 Agents IA actifs</SectionTitle>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 24 }}>
              <AgentCard icon="🎯" name="Agent Commercial" status="actif" color={C.teal}
                action={() => action("send", "Envoi emails")} />
              <AgentCard icon="🏭" name="Agent Fournisseur" status="actif" color={C.purple}
                action={() => action("whatsapp", "WhatsApp fournisseurs")} />
              <AgentCard icon="🌍" name="Agent Intelligence Marché" status="actif" color={C.blue}
                action={() => { setTab("marche"); setMarcheMode("analyse"); }} />
              <AgentCard icon="🔍" name="Agent Prospection Google Maps" status="actif" color={C.gold}
                action={() => action("scan", "Scan Google Maps")} />
              <AgentCard icon="📊" name="Agent Analyse & Mémoire" status="actif" color={C.green}
                action={() => action("analyse", "Analyse pipeline")} />
              <AgentCard icon="🔄" name="Agent Relances (J+3 / J+7)" status="actif" color={C.amber}
                action={() => action("relances", "Lancement relances")} />
            </div>

            <SectionTitle>📋 Recommandations IA en attente</SectionTitle>
            <div style={{ background: C.panel, border: `1px solid ${C.border}`, borderRadius: 14, padding: 16 }}>
              {insights?.recommendations?.items
                ? JSON.parse(insights.recommendations.items || "[]").map((rec, i) => (
                    <div key={i} style={{
                      padding: "10px 14px", borderLeft: `3px solid ${C.gold}`,
                      background: C.card, borderRadius: "0 8px 8px 0",
                      marginBottom: 8, fontSize: 13, color: C.txt,
                    }}>💡 {rec}</div>
                  ))
                : <div style={{ color: C.mid, fontSize: 13 }}>
                    Lance /memoire pour générer les recommandations IA
                  </div>
              }
            </div>
          </div>
        )}

        {/* ═══ PROSPECTS ═══ */}
        {tab === "prospects" && (
          <div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 12, marginBottom: 24 }}>
              <KpiCard icon="📥" label="Total prospects"   value={total}     color={C.teal}   />
              <KpiCard icon="✉️" label="Avec email"        value={withEmail} color={C.blue}   />
              <KpiCard icon="📤" label="Emails envoyés"    value={sent}      color={C.purple} />
              <KpiCard icon="💬" label="Réponses reçues"   value={replied}   color={C.green}  />
            </div>
            <div style={{ background: C.panel, border: `1px solid ${C.border}`, borderRadius: 14, overflow: "hidden" }}>
              <div style={{ padding: "12px 16px", borderBottom: `1px solid ${C.border}`, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <span style={{ fontSize: 13, fontWeight: 700 }}>Tous les prospects · classés par score</span>
                <div style={{ display: "flex", gap: 8 }}>
                  <Btn onClick={() => action("enrich", "Enrichissement")} size="sm">📧 Enrichir</Btn>
                  <Btn onClick={() => action("send", "Envoi")} size="sm" color={C.gold}>📤 Envoyer</Btn>
                </div>
              </div>
              <div style={{ fontSize: 11, color: C.mid, display: "grid", gridTemplateColumns: "2fr 1fr 1fr 1fr 100px", padding: "8px 16px", borderBottom: `1px solid ${C.border}` }}>
                <span>Entreprise</span><span>Produit</span><span>Score IA</span><span>Probabilité</span><span style={{textAlign:"right"}}>Valeur</span>
              </div>
              {[...opps].sort((a,b) => (b.score||0)-(a.score||0)).map(o => (
                <ProspectRow key={o.id} opp={o} onStatusChange={(id, statut) => {
                  setOpps(prev => prev.map(p => p.id === id ? { ...p, status: statut } : p));
                }} />
              ))}
              {opps.length === 0 && (
                <div style={{ color: C.mid, fontSize: 13, padding: "30px", textAlign: "center" }}>
                  Aucun prospect · Lance un scan pour démarrer
                </div>
              )}
            </div>
          </div>
        )}

        {/* ═══ INTELLIGENCE MARCHÉ ═══ */}
        {tab === "marche" && (
          <div>
            <SectionTitle>🌍 Intelligence Marché IA — Enterprise</SectionTitle>

            {/* Mode selector */}
            <div style={{ display: "flex", gap: 8, marginBottom: 16, flexWrap: "wrap" }}>
              {[
                { id: "analyse",     label: "📊 Analyse mondiale" },
                { id: "veille",      label: "🔎 Veille avancée" },
                { id: "ou-vendre",   label: "🌍 Où vendre ? Top 10" },
                { id: "comparateur", label: "🌐 Comparateur pays" },
                { id: "fournisseur", label: "🏭 Trouver fournisseurs" },
                { id: "recommandations", label: "⭐ Recommandations" },
              ].map(m => (
                <button key={m.id} onClick={() => setMarcheMode(m.id)} style={{
                  padding: "8px 14px", borderRadius: 8, cursor: "pointer", fontSize: 12, fontWeight: 700,
                  background: marcheMode === m.id ? C.teal + "25" : "transparent",
                  color: marcheMode === m.id ? C.teal : C.mid,
                  border: `1px solid ${marcheMode === m.id ? C.teal + "60" : C.border}`,
                }}>{m.label}</button>
              ))}
            </div>

            {/* Inputs selon le mode */}
            <div style={{ background: C.panel, border: `1px solid ${C.border}`, borderRadius: 14, padding: 16, marginBottom: 16 }}>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12, marginBottom: 12 }}>
                <div>
                  <div style={{ fontSize: 11, color: C.mid, marginBottom: 5 }}>Produit / secteur / sujet</div>
                  <input value={marcheInput.sujet} onChange={e => setMarcheInput(p=>({...p,sujet:e.target.value}))}
                    placeholder="ex : vanille, cacao, cosmétiques naturels"
                    style={{ width: "100%", padding: "9px 12px", borderRadius: 8, border: `1px solid ${C.border}`, background: C.card, color: C.txt, fontSize: 13 }} />
                </div>
                {["ou-vendre","recommandations"].includes(marcheMode) && (
                  <div>
                    <div style={{ fontSize: 11, color: C.mid, marginBottom: 5 }}>Votre base</div>
                    <input value={marcheInput.origine} onChange={e => setMarcheInput(p=>({...p,origine:e.target.value}))}
                      placeholder="ex : La Réunion, Mayotte"
                      style={{ width: "100%", padding: "9px 12px", borderRadius: 8, border: `1px solid ${C.border}`, background: C.card, color: C.txt, fontSize: 13 }} />
                  </div>
                )}
                {["comparateur","fournisseur"].includes(marcheMode) && (
                  <div>
                    <div style={{ fontSize: 11, color: C.mid, marginBottom: 5 }}>Pays</div>
                    <input value={marcheInput.pays} onChange={e => setMarcheInput(p=>({...p,pays:e.target.value}))}
                      placeholder="ex : Madagascar, Maroc"
                      style={{ width: "100%", padding: "9px 12px", borderRadius: 8, border: `1px solid ${C.border}`, background: C.card, color: C.txt, fontSize: 13 }} />
                  </div>
                )}
                {marcheMode === "veille" && (
                  <div>
                    <div style={{ fontSize: 11, color: C.mid, marginBottom: 5 }}>Région (optionnel)</div>
                    <input value={marcheInput.region} onChange={e => setMarcheInput(p=>({...p,region:e.target.value}))}
                      placeholder="ex : océan Indien, Europe"
                      style={{ width: "100%", padding: "9px 12px", borderRadius: 8, border: `1px solid ${C.border}`, background: C.card, color: C.txt, fontSize: 13 }} />
                  </div>
                )}
              </div>
              <button onClick={lancerMarche} disabled={marcheLoading || !marcheInput.sujet}
                style={{ padding: "10px 24px", borderRadius: 9, border: "none", cursor: "pointer",
                  background: marcheLoading ? C.border : `linear-gradient(135deg,${C.teal},${C.blue})`,
                  color: "#fff", fontWeight: 700, fontSize: 13 }}>
                {marcheLoading ? "⏳ Claude analyse..." : "🚀 Lancer l'analyse"}
              </button>
            </div>

            {/* Résultat */}
            {marcheResult && !marcheResult.error && (
              <div style={{ background: C.panel, border: `1px solid ${C.border}`, borderRadius: 14, padding: 16 }}>
                {marcheResult.note_confiance != null && (
                  <div style={{ marginBottom: 12 }}>
                    <Badge color={marcheResult.note_confiance >= 70 ? C.green : marcheResult.note_confiance >= 40 ? C.amber : C.red}>
                      Confiance {marcheResult.note_confiance}/100
                    </Badge>
                  </div>
                )}
                <pre style={{ fontSize: 12, color: C.txt, whiteSpace: "pre-wrap", lineHeight: 1.6, fontFamily: "inherit" }}>
                  {JSON.stringify(marcheResult, null, 2)}
                </pre>
                <div style={{ fontSize: 11, color: C.mid, marginTop: 12, borderTop: `1px solid ${C.border}`, paddingTop: 10 }}>
                  {marcheResult.disclaimer}
                </div>
              </div>
            )}
            {marcheResult?.error && (
              <div style={{ background: C.redD, border: `1px solid ${C.red}40`, borderRadius: 12, padding: 14, color: C.red }}>
                ❌ {marcheResult.error}
              </div>
            )}
          </div>
        )}

        {/* ═══ PIPELINE KANBAN ═══ */}
        {tab === "pipeline" && (
          <div>
            <SectionTitle>📊 Pipeline commercial — vue Kanban</SectionTitle>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(6,1fr)", gap: 10, marginBottom: 24 }}>
              {[
                { title: "Nouveau",     count: nouveau,  value: 0,                     color: C.blue },
                { title: "Qualifié",    count: qualifie, value: qualifie * 800,         color: C.teal },
                { title: "Proposé",     count: propose,  value: propose * 1200,         color: C.amber },
                { title: "Négociation", count: nego,     value: nego * 1500,            color: C.purple },
                { title: "Gagné ✓",    count: gagne,    value: ca,                     color: C.green },
                { title: "Perdu",       count: perdu,    value: 0,                     color: C.red },
              ].map(col => <PipelineCol key={col.title} {...col} />)}
            </div>

            {/* Prospects par statut */}
            {["Nouveau","Qualifié","Proposé","Négociation"].map(status => {
              const filtered = opps.filter(o => o.status === status);
              if (!filtered.length) return null;
              const colColor = { Nouveau: C.blue, Qualifié: C.teal, Proposé: C.amber, Négociation: C.purple }[status];
              return (
                <div key={status} style={{ marginBottom: 16 }}>
                  <div style={{ fontSize: 12, fontWeight: 700, color: colColor, textTransform: "uppercase", letterSpacing: ".5px", marginBottom: 8 }}>
                    {status} · {filtered.length}
                  </div>
                  <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(260px,1fr))", gap: 10 }}>
                    {filtered.map(o => (
                      <div key={o.id} style={{ background: C.card, border: `1px solid ${colColor}30`, borderRadius: 12, padding: "12px 14px" }}>
                        <div style={{ fontWeight: 700, fontSize: 13, color: C.txt, marginBottom: 4 }}>{o.company}</div>
                        <div style={{ fontSize: 11, color: C.mid }}>{o.region} · {o.sector}</div>
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 8 }}>
                          <Badge color={colColor}>{productLabel(o.bestProduct)}</Badge>
                          <span style={{ fontSize: 13, fontWeight: 700, color: C.gold }}>{money(o.budget || 1000)}</span>
                        </div>
                        {o.contactEmail && <div style={{ fontSize: 10, color: C.mid, marginTop: 6 }}>✉️ {o.contactEmail}</div>}
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        )}

      </div>
    </div>
  );
}
